<template>
  <!-- 인쇄할 때 표시 -->
  <div v-if="(isPrint || readonly) && printFile.length > 0">
    <div ref="printPhotoUploadGridModulHead">
      <div v-if="isShowTitle && !!sectionTitle && customTitle" style="page-break-before: always">
        <slot name="customTitle"></slot>
      </div>
      <div v-else-if="isShowTitle && !!sectionTitle" class="mt-6 mb-4">
        <h5><i class="dot"></i>{{ sectionTitle }}</h5>
      </div>
    </div>
    <ul class="grid grid-cols-2 gap-4 print-photo-grid">
      <li v-for="(file, fileIndex) of printFile" :key="file.id" class="print-photo-item">
        <div class="mb-2" v-if="gridTitle && gridTitle[fileIndex - 1]">{{ gridTitle[fileIndex - 1] }}</div>
        <div class="print-photo-container">
          <img :src="file.path" class="print-photo-image" />
        </div>
        <div v-if="showCustom">
          <slot name="custom" :index="fileIndex"></slot>
        </div>
        <p v-if="showFooterInput" class="print-input mt-2">{{ file.input }}</p>
      </li>
    </ul>
  </div>

  <!-- 작성할 때 표시 -->
  <div v-else-if="!isPrint && !readonly">
    <div v-if="isShowTitle || isCheckMode" class="flex flex-wrap items-center mt-8 mb-4 gap-2">
      <!-- Title -->
      <h5 v-if="isShowTitle && !!sectionTitle" class="flex items-center"><i class="dot"></i>{{ sectionTitle }}</h5>
      <div v-else class="flex-1"></div>

      <!-- Controller -->
      <div v-if="isCheckMode && !readonly && !isPrint" class="ml-auto">
        <vs-button color="secondary" @click="handleAdd"> 사진추가 </vs-button>
        <vs-button class="ml-4" color="secondary" type="border" @click="handleRemove"> 선택삭제 </vs-button>
      </div>
    </div>

    <slot name="customInput"></slot>

    <div class="grid grid-cols-6 xl:grid-cols-12 h-upload gap-5" :class="isShowTitle ? '' : 'mt-6'">
      <div v-for="(file, file_i) in fileList" :key="file_i" class="upload-content" :class="'col-span-' + Math.ceil(12 / setRowCount)">
        <!-- ========== Head ========== -->
        <div class="mb-2" v-if="gridTitle && gridTitle[file_i]">{{ gridTitle[file_i] }}</div>

        <!-- ========== Image ========== -->
        <div class="upload-content__file">
          <vs-checkbox v-if="isCheckMode" v-model="file.checked" :style="'position: absolute; top: 0;'" class="mt-2 z-20" />
          <div class="upload-content__content">
            <img v-if="isPrint" class="w-full h-full printImg" :src="file.thumb" alt="" />
            <FileUploaderTransparent
              v-else
              style="background-size: contain; background-repeat: no-repeat"
              v-model="file.image"
              :readonly="readonly"
              @input="setFile(file_i, file.image)"
              @progress="(state) => { fileProgress(file_i, state) }"
            />
          </div>
          <simple-spinner
            v-if="file.showSpinner"
            style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; background-color: #cdcdcdaa"/>
        </div>


        <div v-if="showCustom">
          <slot name="custom" :index="file_i"></slot>
        </div>
        <!-- ========== Footer ========== -->
        <div v-if="showFooterInput && withDatePicker" class="grid grid-cols-12 gap-x-6 gap-y-4 mt-4">
          <div class="col-span-6 flex items-center">
            <p v-if="isPrint" class="print-input">{{ file.input }}</p>
            <ml-input v-else v-model="file.input" :placeholder="placeholder" class="w-full flex-1"/>
          </div>
          <div class="col-span-6 flex items-center">
            <p v-if="isPrint" class="print-input">{{ file.input }}</p>
            <date-selector v-else class="w-full flex-1" v-model="file.date" :placeholder="placeHolderDatePicker" :readonly="readonly" />
          </div>
        </div>
        <div v-else-if="showFooterInput && !withDatePicker" class="upload-content__input flex justify-center items-center gap-3 mt-2">
          <p v-if="typeof footerBeforeText === 'string' && !!footerBeforeText" style="width: 114px">{{ footerBeforeText }}</p>
          <p v-else-if="0 < footerBeforeText.length && !!footerBeforeText[file_i]" style="width: 114px">{{ footerBeforeText[file_i] }}</p>
          <p v-if="isPrint" class="flex-1">{{ file.input }}</p>
          <ml-input v-else class="flex-1" v-model="file.input" :placeholder="file.placeholder ? file.placeholder : placeholder" :readonly="readonly" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import TemplateFileUploadCheckList from "@/views/modules/template/TemplateFileUploadCheckList";
import TemplateFileUploadCheckListCol from "@/views/modules/template/TemplateFileUploadCheckListCol";
import TemplateFileUploadGallery from "@/views/modules/template/TemplateFileUploadGallery";
import FileUploaderTransparent from "@/components/FileUploaderTransparent2";
import DateSelector from "@/components/selector/DateSelector";
import SimpleSpinner from "@/components/spinner/SimpleSpinner.vue";

export default {
  name: "PhotoUploadGridModule",
  components: {
    SimpleSpinner,
    TemplateFileUploadGallery,
    TemplateFileUploadCheckListCol,
    TemplateFileUploadCheckList,
    FileUploaderTransparent,
    DateSelector,
  },
  props: {
    isCheckMode: {
      type: Boolean,
      default: true,
    },
    type: {
      type: String,
      default: "check_list",
    },
    /** 이미지 업로드 그리드 개수 */
    gridCount: { type: Number, default: 4 },

    /** 한 줄에 표시할 셀의 개수 (1 ~ 4) */
    rowCount: { type: Number, default: 2 },

    /** 맨 위에 표시할 동그라미 제목 */
    sectionTitle: String,

    /** 파일 형식 상관없이 업로드할 수 있는 경우 TRUE */
    fileTypeAll: {
      type: Boolean,
      default: false,
    },
    withDatePicker: {
      type: Boolean,
      default: false,
    },

    /** 맨 위의 동그라미 제목 표시 여부 */
    isShowTitle: {
      type: Boolean,
      default: true,
    },
    customTitle: {
      type: Boolean,
      default: false,
    },

    /** 인쇄용 화면일 경우 TRUE */
    isPrint: Boolean,

    showFooterInput: {
      type: Boolean,
      default: true,
    },
    footerBeforeText: {
      type: Array | String,
      default: () => [],
    },
    showCustom: {
      type: Boolean,
      default: false,
    },
    /** 입력 창에 표시할 힌트 */
    placeholder: String,

    placeHolderDatePicker: {
      type: String,
      default: "날짜선택",
    },

    readonly: Boolean,
    id: String,
    value: Array, //{ id:0, fileName:'test1.txt', fileSize:1234, path:'', thumb:'', input:'' }
    multiStage: Number,

    /** 인풋창 제거 */
    notInput: Boolean,
    /** 그리드내 타이틀 */
    gridTitle: Array,
  },
  watch: {
    value: {
      handler(value) {
        this.fileList = value;
      },
    },
    fileList: {
      immediate: true,
      deep: true,
      handler(value) {
        this.$emit("input", value);
      },
    },
    isPrint: {
      immediate: true,
      deep: true,
      handler(value) {
        if (value) {
          this.$nextTick(() => {
            if (this.$refs.printPhotoUploadGridModulHead) this.printHeadHeight = (this.$refs.printPhotoUploadGridModulHead.clientHeight + 100) / 2;
            else this.printHeadHeight = 80;
          });
        }
      },
    },
  },
  computed: {
    printFile() {
      return this.fileList.filter((file) => !!file.id);
    },
    setRowCount() {
      if (4 < this.rowCount) return 4;
      else if (this.rowCount < 1) return 1;
      else return this.rowCount;
    },
  },
  data() {
    return {
      printHeadHeight: 80,
      fileList: this.value,
      checkIndex: [],
    };
  },
  created() {
    this.fillEmptyFileList();
  },
  methods: {
    fillEmptyFileList() {
      for (let index = this.fileList.length; index < this.gridCount; index++) {
        this.fileList.push({showSpinner: false});
      }
    },
    handleAdd() {
      this.fileList.push({showSpinner: false});
    },
    handleRemove() {
      this.fileList = this.fileList.filter((fl) => !fl.checked);
    },
    setFile(index, file) {
      this.fileList[index].id = file ? file.id : null;
      this.fileList[index].path = file ? file.path : null;
      this.fileList[index].fileName = file ? file.fileName : null;
    },
    fileProgress (index, state) {
      console.log("fileProgress", index, state)
      if (state === 'start') {
        this.fileList[index].showSpinner = true;
      } else if (state === 'success') {
        this.fileList[index].showSpinner = false;
        this.$toast.open({type: 'default', message: '이미지 업로드가 완료되었습니다.', position: 'bottom', duration: 2000});
      } else if (state === 'fail') {
        this.fileList[index].showSpinner = false;
        this.$toast.open({type: 'error', message: '이미지 업로드가 완료되었습니다.', position: 'bottom', duration: 2000});
      }
    },
    // 읽기 전용에서, 이미지가 클릭됐을 때 새 창으로 이미지 표시
    onClickImage(src) {
      window.open(src, "_blank");
    },
  },
};
</script>

<style scoped lang="scss">
.upload-content__file {
  width: 100%;
  padding-top: 56.25%;
  position: relative;
  border: 1px solid #cdcdcd;
  border-radius: 6px;

  .upload-content__content {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-position: center center;
    background-size: cover;

    .printImg {
      background-color: white;
      border-radius: 6px;
    }
  }
}

/* 인쇄용 사진 레이아웃 */
.print-photo-grid {
  /* 그리드 기본 설정 유지 */
}

.print-photo-item {
  display: flex;
  flex-direction: column;
  margin-bottom: 15px;
  break-inside: avoid;
  page-break-inside: avoid;
}

.print-photo-container {
  width: 100%;
  /* 4:3 비율 (높이 = 너비 * 0.75) */
  padding-top: 75%;
  position: relative;
  border: 1px solid #cdcdcd;
  border-radius: 6px;
  background: white;
  overflow: hidden;
}

.print-photo-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
}

/* 인쇄 시 추가 최적화 */
@media print {
  .print-photo-grid {
    margin-bottom: 10px;
  }

  .print-photo-item {
    margin-bottom: 10px;
    break-inside: avoid;
    page-break-inside: avoid;
  }

  .print-photo-container {
    /* 인쇄 시에도 4:3 비율 유지 */
    padding-top: 75%;
    border: 1px solid #cdcdcd;
  }

  .print-photo-image {
    /* 인쇄 시 이미지 품질 최적화 */
    print-color-adjust: exact;
  }
}
</style>
